import { 
  EmailSubmissionRequest, 
  EmailSubmissionResponse, 
  QuestionsSubmissionRequest, 
  QuestionsSubmissionResponse,
  HealthCheckResponse,
  WaitlistStatsResponse,
  ApiError,
  ApiErrorType,
  WatchLaterSize,
  EmailVerificationRequest,
  EmailVerificationResponse,
  VerificationStatsResponse,
  ReferralCodeVerificationResponse,
  EmailVerificationForQuestionsRequest,
  EmailVerificationForQuestionsResponse
} from '../types/waitlist';

// Error handling utility
export const handleApiError = (error: any): ApiError => {
  if (error.response) {
    const data = error.response.data;
    switch (data.error) {
      case 'RATE_LIMIT_EXCEEDED':
        return {
          type: 'RATE_LIMIT_EXCEEDED',
          message: 'Too many requests. Please try again later.'
        };
      case 'INVALID_SESSION':
        return {
          type: 'INVALID_SESSION',
          message: 'Session expired. Please submit your email again.'
        };
      case 'DUPLICATE_SUBMISSION':
        return {
          type: 'DUPLICATE_SUBMISSION',
          message: 'You have already submitted this information.'
        };
      case 'VALIDATION_ERROR':
        return {
          type: 'VALIDATION_ERROR',
          message: data.message || 'Validation error occurred.'
        };
      default:
        return {
          type: 'UNKNOWN_ERROR',
          message: data.message || 'An error occurred. Please try again.'
        };
    }
  }
  return {
    type: 'NETWORK_ERROR',
    message: 'Network error. Please check your connection.'
  };
};

// Single consolidated API service for your backend
export class WaitlistAPI {
  private baseUrl: string;

  constructor(baseUrl: string = 'http://localhost:8000') {
    this.baseUrl = baseUrl;
    console.log(`WaitlistAPI initialized with baseUrl: ${baseUrl}`);
    console.log(`Environment VITE_API_BASE_URL:`, import.meta.env.VITE_API_BASE_URL);
  }

  async submitEmail(email: string, keepUpdated: boolean, referralCode?: string): Promise<EmailSubmissionResponse> {
    try {
      const payload: EmailSubmissionRequest = {
        email,
        keepUpdated,
        referralCode,
        metadata: {
          userAgent: navigator.userAgent,
          referrer: document.referrer || undefined,
        }
      };

      console.log('Making request to:', `${this.baseUrl}/api/waitlist/email`);
      const response = await fetch(`${this.baseUrl}/api/waitlist/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        // Store sessionId for questions submission
        localStorage.setItem('waitlist_session_id', data.data.sessionId);
        return data;
      } else {
        // Return the response object with success: false so the UI can handle the error message
        return data;
      }
    } catch (error) {
      console.error('Email submission failed:', error);
      const apiError = handleApiError(error);
      throw new Error(apiError.message);
    }
  }

  async submitQuestions(answers: {
    watchLaterSize: WatchLaterSize;
    whatWouldYouQuellect: string;
    nextPlatforms: string[];
    primaryUseCase: string;
    role: string;
    otherThoughts?: string;
  }): Promise<QuestionsSubmissionResponse> {
    try {
      const sessionId = localStorage.getItem('waitlist_session_id');
      if (!sessionId) {
        throw new Error('No session ID found. Please submit email first.');
      }

      const payload: QuestionsSubmissionRequest = {
        sessionId,
        answers,
        metadata: {
          userAgent: navigator.userAgent,
          referrer: document.referrer || undefined,
        }
      };

      const response = await fetch(`${this.baseUrl}/api/waitlist/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        // Return the response object with success: false so the UI can handle the error message
        return data;
      }
    } catch (error) {
      console.error('Questions submission failed:', error);
      const apiError = handleApiError(error);
      throw new Error(apiError.message);
    }
  }

  async verifyEmail(token: string): Promise<EmailVerificationResponse> {
    try {
      const payload: EmailVerificationRequest = { token };
      
      console.log(`[${new Date().toISOString()}] verifyEmail called with token:`, token);
      console.log(`[${new Date().toISOString()}] Making request to:`, `${this.baseUrl}/api/confirm-email`);
      
      const response = await fetch(`${this.baseUrl}/api/confirm-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log(`[${new Date().toISOString()}] Response status:`, response.status);
      const data = await response.json();
      console.log(`[${new Date().toISOString()}] Response data:`, data);
      
      return data;
    } catch (error) {
      console.error('Email verification failed:', error);
      const apiError = handleApiError(error);
      throw new Error(apiError.message);
    }
  }

  async getVerificationStats(email: string): Promise<VerificationStatsResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/verification-stats/${encodeURIComponent(email)}`);
      
      if (!response.ok) {
        throw new Error('Failed to get verification stats');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Verification stats fetch failed:', error);
      const apiError = handleApiError(error);
      throw new Error(apiError.message);
    }
  }

  async verifyEmailSubscription(email: string): Promise<EmailVerificationForQuestionsResponse> {
    try {
      console.log(`[${new Date().toISOString()}] verifyEmailSubscription called with email:`, email);
      console.log(`[${new Date().toISOString()}] Making request to:`, `${this.baseUrl}/api/waitlist/verify-email`);
      console.log(`[${new Date().toISOString()}] Base URL:`, this.baseUrl);
      
      // First, let's test if we can reach the backend at all
      console.log(`[${new Date().toISOString()}] Testing backend connectivity...`);
      try {
        const healthResponse = await fetch(`${this.baseUrl}/health`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        });
        console.log(`[${new Date().toISOString()}] Health check status:`, healthResponse.status);
        if (healthResponse.ok) {
          const healthData = await healthResponse.json();
          console.log(`[${new Date().toISOString()}] Health check data:`, healthData);
        }
      } catch (healthError) {
        console.error(`[${new Date().toISOString()}] Health check failed:`, healthError);
      }
      
      const payload: EmailVerificationForQuestionsRequest = { email };
      console.log(`[${new Date().toISOString()}] Payload:`, payload);
      
      const response = await fetch(`${this.baseUrl}/api/waitlist/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log(`[${new Date().toISOString()}] Response status:`, response.status);
      console.log(`[${new Date().toISOString()}] Response headers:`, Object.fromEntries(response.headers.entries()));
      const data = await response.json();
      console.log(`[${new Date().toISOString()}] Response data:`, data);
      
      return data;
    } catch (error) {
      console.error('Email verification failed:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return {
        success: false,
        message: 'Failed to verify email. Please try again.',
        data: null
      };
    }
  }

  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      const data: HealthCheckResponse = await response.json();
      return data.success;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  async getStats(): Promise<WaitlistStatsResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/waitlist/stats`);
      const data: WaitlistStatsResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Stats fetch failed:', error);
      throw new Error('Failed to fetch statistics');
    }
  }

  // Utility method to clear session
  clearSession() {
    localStorage.removeItem('waitlist_session_id');
  }

  // Utility method to check if session exists
  hasSession(): boolean {
    return !!localStorage.getItem('waitlist_session_id');
  }
}

// Export the single API service instance
export const waitlistAPI = new WaitlistAPI(import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000');
